import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { crossReferralApi } from '../../api/crossReferral.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Spinner } from '../../components/ui/index';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

function hasLink(links, fromId, toId) {
  return links.some((l) => Number(l.from_department_id) === Number(fromId) && Number(l.to_department_id) === Number(toId));
}

function toggleLink(links, fromId, toId) {
  if (hasLink(links, fromId, toId)) {
    return links.filter((l) => !(Number(l.from_department_id) === Number(fromId) && Number(l.to_department_id) === Number(toId)));
  }
  return [...links, { from_department_id: fromId, to_department_id: toId }];
}

function outgoingPairs(departments, fromId) {
  return departments
    .filter((d) => Number(d.id) !== Number(fromId))
    .map((d) => ({ from_department_id: fromId, to_department_id: d.id }));
}

function allPairs(departments) {
  return departments.flatMap((from) => outgoingPairs(departments, from.id));
}

function rowIsComplete(links, departments, fromId) {
  return outgoingPairs(departments, fromId).every((p) => hasLink(links, p.from_department_id, p.to_department_id));
}

function setRowLinks(links, departments, fromId, enable) {
  const rest = links.filter((l) => Number(l.from_department_id) !== Number(fromId));
  return enable ? [...rest, ...outgoingPairs(departments, fromId)] : rest;
}

function matrixIsComplete(links, departments) {
  const needed = allPairs(departments);
  return needed.length > 0 && needed.every((p) => hasLink(links, p.from_department_id, p.to_department_id));
}

const smallBtn = {
  padding: '4px 8px',
  borderRadius: 6,
  border: '1px solid #bbf7d0',
  background: '#ecfdf5',
  color: '#166534',
  fontSize: 11,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

function DeptMatrix({ departments, links, onToggle, onToggleRow, onEnableAll, disabled }) {
  if (!departments.length) {
    return <div style={{ fontSize: 13, color: '#94a3b8' }}>دپارتمانی برای تنظیم وجود ندارد.</div>;
  }
  const allOn = matrixIsComplete(links, departments);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button type="button" disabled={disabled} onClick={onEnableAll} style={smallBtn}>
          {allOn ? 'لغو همه ارجاعات این جدول' : 'فعال‌سازی همه ارجاعات این جدول'}
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'right', padding: '8px 10px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>از ＼ به</th>
              <th style={{ padding: '8px 6px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>همه</th>
              {departments.map((d) => (
                <th key={d.id} style={{ padding: '8px 6px', color: '#334155', fontWeight: 600, whiteSpace: 'nowrap' }}>{d.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {departments.map((from) => {
              const rowOn = rowIsComplete(links, departments, from.id);
              return (
                <tr key={from.id}>
                  <td style={{ padding: '8px 10px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', borderTop: '1px solid #e2e8f0' }}>{from.name}</td>
                  <td style={{ textAlign: 'center', padding: '8px 6px', borderTop: '1px solid #e2e8f0' }}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => onToggleRow(from.id, !rowOn)}
                      style={{ ...smallBtn, background: rowOn ? '#fef2f2' : '#ecfdf5', borderColor: rowOn ? '#fecaca' : '#bbf7d0', color: rowOn ? '#b91c1c' : '#166534' }}
                    >
                      {rowOn ? 'لغو همه' : 'فعال همه'}
                    </button>
                  </td>
                  {departments.map((to) => (
                    <td key={to.id} style={{ textAlign: 'center', padding: '8px 6px', borderTop: '1px solid #e2e8f0' }}>
                      {from.id === to.id ? (
                        <span style={{ color: '#cbd5e1' }}>—</span>
                      ) : (
                        <input
                          type="checkbox"
                          disabled={disabled}
                          checked={hasLink(links, from.id, to.id)}
                          onChange={() => onToggle(from.id, to.id)}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CrossReferralSettingsPage() {
  const { addToast } = useUI();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schemaReady, setSchemaReady] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [managersOn, setManagersOn] = useState(false);
  const [employeesOn, setEmployeesOn] = useState(false);
  const [managerLinks, setManagerLinks] = useState([]);
  const [employeeLinks, setEmployeeLinks] = useState([]);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await crossReferralApi.getSettings();
      const data = res.data || {};
      setSchemaReady(data.schema_ready !== false);
      setDepartments(Array.isArray(data.departments) ? data.departments : []);
      setManagersOn(!!data.managers_can_refer_cross);
      setEmployeesOn(!!data.employees_can_refer_cross);
      setManagerLinks(Array.isArray(data.manager_links) ? data.manager_links : []);
      setEmployeeLinks(Array.isArray(data.employee_links) ? data.employee_links : []);
    } catch {
      if (showSpinner) addToast('خطا در بارگذاری تنظیمات ارجاع بین دپارتمان‌ها', 'error');
    }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(false), { paused: saving });

  const save = async () => {
    setSaving(true);
    try {
      const res = await crossReferralApi.saveSettings({
        managers_can_refer_cross: managersOn,
        employees_can_refer_cross: employeesOn,
        manager_links: managerLinks,
        employee_links: employeeLinks,
      });
      const data = res.data || {};
      setSchemaReady(data.schema_ready !== false);
      setManagersOn(!!data.managers_can_refer_cross);
      setEmployeesOn(!!data.employees_can_refer_cross);
      setManagerLinks(Array.isArray(data.manager_links) ? data.manager_links : []);
      setEmployeeLinks(Array.isArray(data.employee_links) ? data.employee_links : []);
      addToast('تنظیمات ارجاع ذخیره شد', 'success');
    } catch {
      addToast('خطا در ذخیره تنظیمات', 'error');
    }
    setSaving(false);
  };

  const enableAllReferrals = () => {
    const pairs = allPairs(departments);
    setManagersOn(true);
    setEmployeesOn(true);
    setManagerLinks(pairs);
    setEmployeeLinks(pairs);
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="ارجاع بین دپارتمان‌ها"
        subtitle="تعیین کنید مدیران کدام دپارتمان‌ها می‌توانند به هم ارجاع دهند، و کارمندان کدام دپارتمان می‌توانند تیکت را به دپارتمان دیگری بفرستند"
        action={<Link to="/settings" style={{ fontSize: 13, color: '#64748b' }}>بازگشت به تنظیمات</Link>}
      />

      {!schemaReady && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 12,
          padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#92400e',
        }}
        >
          جدول تنظیمات روی سرور ساخته نشده — پس از اجرای SQL این صفحه کامل کار می‌کند.
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 960 }}>
          <div style={{
            background: '#fff', borderRadius: 14, border: '1px solid #bbf7d0', padding: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>فعال‌سازی همه ارجاعات</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                هر دو نوع ارجاع (مدیران به هم، و کارمندان به دپارتمان دیگر) برای همه دپارتمان‌ها روشن می‌شود.
              </div>
            </div>
            <Btn onClick={enableAllReferrals} disabled={!schemaReady || !departments.length}>فعال‌سازی همه ارجاعات</Btn>
          </div>

          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={managersOn} onChange={(e) => setManagersOn(e.target.checked)} style={{ marginTop: 3 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>مدیران دپارتمان می‌توانند به هم ارجاع دهند</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.7 }}>
                  اگر فعال باشد، فقط جفت‌دپارتمان‌هایی که در جدول زیر تیک خورده‌اند مجازند. ارجاع به مدیر خود دپارتمان همیشه مجاز است.
                </div>
              </div>
            </label>
            <div style={{ marginTop: 16, opacity: managersOn ? 1 : 0.45, pointerEvents: managersOn ? 'auto' : 'none' }}>
              <DeptMatrix
                departments={departments}
                links={managerLinks}
                disabled={!managersOn}
                onToggle={(fromId, toId) => setManagerLinks((prev) => toggleLink(prev, fromId, toId))}
                onToggleRow={(fromId, enable) => setManagerLinks((prev) => setRowLinks(prev, departments, fromId, enable))}
                onEnableAll={() => setManagerLinks((prev) => (matrixIsComplete(prev, departments) ? [] : allPairs(departments)))}
              />
            </div>
          </div>

          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={employeesOn} onChange={(e) => setEmployeesOn(e.target.checked)} style={{ marginTop: 3 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>کارمندان می‌توانند به دپارتمان دیگر ارجاع دهند</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.7 }}>
                  اگر فعال باشد، کارمند دپارتمان مبدأ می‌تواند تیکت را به مدیر دپارتمان مقصد بفرستد — فقط برای خانه‌های تیک‌خورده. ارجاع به مدیر بالاسری خودشان همیشه مجاز است.
                </div>
              </div>
            </label>
            <div style={{ marginTop: 16, opacity: employeesOn ? 1 : 0.45, pointerEvents: employeesOn ? 'auto' : 'none' }}>
              <DeptMatrix
                departments={departments}
                links={employeeLinks}
                disabled={!employeesOn}
                onToggle={(fromId, toId) => setEmployeeLinks((prev) => toggleLink(prev, fromId, toId))}
                onToggleRow={(fromId, enable) => setEmployeeLinks((prev) => setRowLinks(prev, departments, fromId, enable))}
                onEnableAll={() => setEmployeeLinks((prev) => (matrixIsComplete(prev, departments) ? [] : allPairs(departments)))}
              />
            </div>
          </div>

          <div>
            <Btn onClick={save} loading={saving} disabled={!schemaReady}>ذخیره تنظیمات</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
